// Single-run API — the /test flow. Used by New Test, the dashboard run lists,
// and the Chrome extension. POST returns a run_id; poll getRun() until terminal.

import { api, filenameFromDisposition, getApiBase, getToken } from "./client";
import { formatDateTime } from "@/lib/dates";

// startRun(url, instruction, { projectId, credentialId, sessionState, freshSession })
// sessionState is the extension's captured live browser session (Playwright
// storage_state) — present only for extension runs that reuse a logged-in tab.
// freshSession=true asks the engine to start logged-out (no inherited cookies,
// no automatic pre-login, no auth_state.json save-back). Use it when the test
// is supposed to observe the public / sign-in surface — e.g. "log in with a
// wrong password" — instead of landing on an already-authenticated dashboard.
export const startRun = (url, instruction, opts = {}) =>
  api.post("/test/run", {
    url,
    instruction,
    project_id: opts.projectId || undefined,
    credential_id: opts.credentialId || undefined,
    session_state: opts.sessionState || undefined,
    fresh_session: !!opts.freshSession,
  });

// Multi-tab run: tabs is [{ url, instruction? }]; one shared authenticated
// session across all of them. Returns { run_ids, count, ... }.
// freshSession=true is forwarded to every per-tab TestRunRequest so every
// tab in the batch starts logged-out.
export const startBatch = (tabs, instruction, opts = {}) =>
  api.post("/test/run-batch", {
    tabs,
    instruction,
    project_id: opts.projectId || undefined,
    credential_id: opts.credentialId || undefined,
    session_state: opts.sessionState || undefined,
    fresh_session: !!opts.freshSession,
  });

export const getRun = (id) => api.get(`/test/runs/${id}`);

export const isTerminal = (status) =>
  ["passed", "failed", "error", "inconclusive", "cancelled", "interrupted", "blocked"].includes(
    String(status).toLowerCase()
  );

function formatWhen(iso) {
  if (!iso) return undefined;
  return formatDateTime(iso, iso);
}

// Map a backend run (list item OR full detail) into the UI run shape used by
// RunRow / RunDetail / StepTimeline / BugReportCard. Tolerant of missing
// detail fields (list items have no step_results / token_usage / bug_report).
export function normalizeRunDetail(r) {
  const tu = r.token_usage || {};
  const tokens = (tu.total_input_tokens || 0) + (tu.total_output_tokens || 0);
  return {
    id: r.id,
    status: String(r.status || "").toUpperCase(),
    instruction: r.instruction,
    url: r.url,
    durationSeconds: r.duration_seconds,
    cost: tu.estimated_cost_usd,
    tokens: tokens || undefined,
    createdAt: formatWhen(r.created_at),
    createdAtRaw: r.created_at, // ISO, for date bucketing in trends
    // Console / uncaught-JS / failed-network evidence, shown by RunDetail so a
    // silent failure (a create that 500s while the UI looked fine) is visible.
    evidence: r.evidence,
    // project_id is forwarded so the Re-run button can re-attach the new
    // run to the same project (otherwise it would land as a project-less run
    // and never show up in the project's results panel again).
    projectId: r.project_id,

    byAgent: tu.by_agent || {}, // { agent: { calls, input_tokens, output_tokens, cost_usd } }
    steps: (r.step_results || []).map((s) => ({
      index: s.step_index,
      action: s.action,
      status: s.status,
      reason: s.reason,
      screenshot_path: s.screenshot_path,
      // Passed through so the step timeline can render a developer-readable
      // card (objective / action / expected). The API already returns these;
      // they were previously dropped here.
      target: s.target,
      input_value: s.input_value,
      user_action_description: s.user_action_description,
    })),
    bug: r.bug_report || null,
  };
}

// Raw run list (backend shape: id/status/url/instruction/steps_passed/
// steps_failed/duration_seconds/created_at). Used by the tester Runs & Bugs
// page's dashboard flow, which mirrors the admin page and renders those raw
// fields directly. mine=true scopes to the caller's OWN runs (user_id) instead
// of the whole company — that's how a tester sees only the cases they tested.
export const listTestRuns = ({ mine = false, limit = 200 } = {}) =>
  api.get("/test/runs", { params: { limit, mine: mine || undefined } });

// List recent runs, then hydrate each with its detail (steps + bug + cost) so
// the dashboard, runs table, and bugs board all share one fully-shaped source.
// Pass `projectId` to scope to a single project (used by the grouped Runs &
// Bugs page so each project section gets its own filtered list).
export async function listRunsDetailed({ limit = 25, projectId } = {}) {
  const list = await api.get("/test/runs", {
    params: { limit, project_id: projectId || undefined },
  });
  const details = await Promise.allSettled(list.map((r) => getRun(r.id)));
  return details.map((d, i) =>
    normalizeRunDetail(d.status === "fulfilled" ? d.value : list[i])
  );
}

// Bugs straight from the bug_reports table (single source of truth) so the count
// matches the admin dashboard instead of "bugs within the latest 25 runs". Each
// item is a fully-shaped run (it HAS a bug) plus a `resolved` flag computed
// server-side across ALL runs. Pass projectId to scope to one project.
export async function listBugs({ projectId, limit = 1000, mine = false } = {}) {
  const list = await api.get("/test/bugs", {
    params: { project_id: projectId || undefined, limit, mine: mine || undefined },
  });
  return list.map((r) => ({
    ...normalizeRunDetail(r),
    resolved: !!r.resolved_on_rerun,
  }));
}


async function downloadFile(path, fallbackName) {
  const origin =
    typeof window !== "undefined" ? window.location.origin : "http://localhost";
  const url = new URL(getApiBase() + path, origin);
  const token = getToken();
  const res = await fetch(url.toString(), {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) throw new Error(`Download failed (${res.status})`);
  const blob = await res.blob();
  const dispo = res.headers.get("Content-Disposition") || "";
  const name = filenameFromDisposition(dispo, fallbackName);
  const objUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = objUrl;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(objUrl);
}


export const downloadRunReport = (runId, format) =>
  downloadFile(`/test/runs/${runId}/report.${format}`, `run-${runId.slice(0, 8)}.${format}`);
